import { writable } from 'svelte/store';
import { getFromStorage, saveToStorage } from '../utils/localStorage';
import { themeSettingsStore } from './themeSettingsStore';

export type Theme = 'light' | 'dark';

const DEFAULT_THEME: Theme = 'dark';

/**
 * Initialize theme from localStorage or system preference
 */
function initTheme(): Theme {
  const stored = getFromStorage<Theme>('theme', DEFAULT_THEME);
  
  // If no stored theme, check system preference
  if (stored === DEFAULT_THEME && typeof window !== 'undefined' && window.matchMedia) {
    try {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? 'dark' : 'light';
    } catch {
      // Fallback if matchMedia is not available
      return DEFAULT_THEME;
    }
  }
  
  return stored;
}

/**
 * Apply theme to document and custom colors for both light and dark mode
 */
function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  
  // Apply or remove dark class based on theme
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  
  // Apply custom colors for current theme
  // Use requestAnimationFrame to ensure DOM is updated before applying colors
  requestAnimationFrame(() => {
    try {
      themeSettingsStore.reapplyColors();
    } catch (error) {
      console.warn('Failed to apply custom colors:', error);
    }
  });
}

/**
 * Theme store with localStorage persistence
 */
function createThemeStore() {
  const initialTheme = initTheme();
  applyTheme(initialTheme);
  
  const { subscribe, set, update } = writable<Theme>(initialTheme);

  return {
    subscribe,
    
    setTheme: (theme: Theme) => {
      applyTheme(theme);
      saveToStorage('theme', theme);
      set(theme);
    },
    
    toggleTheme: () => {
      update((currentTheme) => {
        const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
        applyTheme(newTheme);
        saveToStorage('theme', newTheme);
        return newTheme;
      });
    },
    
    reset: () => {
      applyTheme(DEFAULT_THEME);
      saveToStorage('theme', DEFAULT_THEME);
      set(DEFAULT_THEME);
    },
  };
}

export const themeStore = createThemeStore();
